import React, { useState } from "react";
import { Input, Textarea, Select, Checkbox, Button, Label, toast } from "@/components/ds";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type KnowledgeEntry } from "@/lib/api";
import { FALLBACK_CATEGORIES } from "./constants";
import { Field } from "../onboarding/_ui";

export function EntryForm({
  entry, categories, onSaved, onCancel,
}: {
  entry?: KnowledgeEntry | null;
  categories: { value: string; label: string }[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { role } = useAuth();
  const isOwner = role === "owner";
  const [form, setForm] = useState({
    title: entry?.title ?? "",
    content: entry?.content ?? "",
    category: entry?.category ?? "custom",
    customer_visible: entry?.customer_visible ?? true,
    internal_only: entry?.internal_only ?? false,
    effective_date: entry?.effective_at ? entry.effective_at.slice(0, 10) : "",
    expiration_date: entry?.expires_at ? entry.expires_at.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const cats = categories.length ? categories : FALLBACK_CATEGORIES;
  const valid = form.title.trim().length >= 2 && form.content.trim().length >= 1;

  const submit = async (approve: boolean) => {
    if (!valid) { toast.error("Add a title and content."); return; }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        title: form.title.trim(), content: form.content.trim(), category: form.category,
        customer_visible: form.customer_visible, internal_only: form.internal_only,
        effective_date: form.effective_date || null, expiration_date: form.expiration_date || null,
      };
      if (entry) {
        await api.kbUpdate(entry.id, body);
        if (approve) await api.kbApprove(entry.id);
      } else {
        const created = await api.kbCreate({ ...body, status: approve ? "approved" : "draft" });
        if (approve && created.approval_status !== "approved") await api.kbApprove(created.id);
      }
      toast.success(approve ? "Entry approved" : "Draft saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const needsOwnerToApprove = form.customer_visible && !form.internal_only && !isOwner;

  return (
    <div className="space-y-4" data-testid="entry-form">
      <Field label="Title" required testid="field-kb-title">
        <Input value={form.title} onChange={(e) => set("title", e.target.value)}
          placeholder="e.g. Do you offer emergency service?" data-testid="kb-title" />
      </Field>
      <Field label="Content" required testid="field-kb-content">
        <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={5}
          placeholder="The approved answer your AI can rely on." data-testid="kb-content" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Category" testid="field-kb-category">
          <Select value={form.category} onChange={(v) => set("category", v)} options={cats} testid="kb-category" />
        </Field>
        <Field label="Effective date" hint="Optional" testid="field-kb-effective">
          <Input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid="kb-effective" />
        </Field>
        <Field label="Expiration date" hint="Optional" testid="field-kb-expiration">
          <Input type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} data-testid="kb-expiration" />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
          <Checkbox checked={form.customer_visible} onChange={(v) => set("customer_visible", v)} testid="kb-customer-visible" />
          <span className="text-sm text-slate-700">Customer usable</span>
        </label>
        <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
          <Checkbox checked={form.internal_only} onChange={(v) => { set("internal_only", v); if (v) set("customer_visible", false); }} testid="kb-internal-only" />
          <span className="text-sm text-slate-700">Internal only</span>
        </label>
      </div>
      {needsOwnerToApprove && (
        <p className="text-xs text-amber-600" data-testid="owner-approval-note">
          Only the organization owner can approve customer-facing knowledge. You can save it as a draft.
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={saving} data-testid="kb-cancel">Cancel</Button>
        <Button variant="outline" onClick={() => submit(false)} disabled={saving} data-testid="kb-save-draft">Save draft</Button>
        <Button onClick={() => submit(true)} disabled={saving || needsOwnerToApprove} data-testid="kb-save-approve">
          {saving ? "Saving…" : "Save & approve"}
        </Button>
      </div>
    </div>
  );
}
